import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Search, CornerDownLeft } from "lucide-react";

/* ⌘K — jump to any view, officer or partner, or fire an action, by typing. */
export const CommandPalette = ({ open, onClose, views = [], mlos = [], bdms = [], leads = [], codes = [], actions = [] }) => {
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  const items = useMemo(() => {
    const all = [
      ...actions.map((a) => ({ ...a, kind: "Action" })),
      ...views.map((v) => ({ label: v.label, group: v.group, kind: "Go to", run: v.run, hint: v.hint })),
      ...mlos.map((m) => ({
        label: m.name || m.slug,
        hint: `/${m.slug}${m.licensed_states?.length ? ` · ${m.licensed_states.join(", ")}` : ""}`,
        kind: "Officer",
        run: m.run,
      })),
      ...bdms.map((b) => ({
        label: b.full_name || b.email,
        hint: [b.company, b.city_state].filter(Boolean).join(" · "),
        kind: "Partner",
        run: b.run,
      })),
      ...codes.map((c) => ({
        label: c.code,
        hint: [c.full_name, c.mlo_name || "House", c.bdm_name || "Direct", c.status]
          .filter(Boolean)
          .join(" · "),
        kind: "Code",
        run: c.run,
      })),
      ...leads.map((l) => ({
        label: l.full_name || l.email,
        hint: [l.email, l.phone, l.grc_code, l.contact_status].filter(Boolean).join(" · "),
        kind: "Lead",
        run: l.run,
      })),
    ];
    const needle = q.trim().toLowerCase();
    if (!needle) return all.slice(0, 12);
    return all
      .filter((i) => `${i.label} ${i.hint || ""} ${i.kind}`.toLowerCase().includes(needle))
      .slice(0, 14);
  }, [q, views, mlos, bdms, leads, codes, actions]);

  useEffect(() => {
    if (open) {
      setQ("");
      setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  useEffect(() => setCursor(0), [q]);

  const choose = (item) => {
    onClose();
    item?.run?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(items[cursor]);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-navy-deep/60 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4"
          onClick={onClose}
          data-testid="command-palette-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: -14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl rounded-2xl bg-white border border-linen-border shadow-2xl overflow-hidden"
            data-testid="command-palette"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-linen-border">
              <Search size={16} className="text-teal shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Jump to a view, an officer, a partner — or type an action"
                data-testid="command-palette-input"
                className="flex-1 text-sm text-navy placeholder:text-ink-400 bg-transparent focus:outline-none"
              />
              <kbd className="text-[10px] font-bold text-ink-400 bg-linen border border-linen-border rounded px-1.5 py-0.5">esc</kbd>
            </div>

            <div className="max-h-[52vh] overflow-y-auto py-1.5">
              {items.length === 0 ? (
                <div className="px-4 py-6 text-sm text-ink-500" data-testid="command-palette-empty">
                  Nothing matches “{q}”.
                </div>
              ) : (
                items.map((item, i) => (
                  <button
                    key={`${item.kind}-${item.label}-${i}`}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => choose(item)}
                    data-testid={`command-palette-item-${i}`}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${
                      i === cursor ? "bg-linen" : "hover:bg-linen/60"
                    }`}
                  >
                    <span className="text-[9px] font-bold uppercase tracking-wider text-teal w-14 shrink-0">
                      {item.kind}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm font-medium text-navy truncate ${item.kind === "Code" ? "font-mono tracking-wider" : ""}`}>
                        {item.label}
                      </span>
                      {item.hint ? <span className="block text-[11px] text-ink-500 truncate">{item.hint}</span> : null}
                    </span>
                    {i === cursor ? (
                      <CornerDownLeft size={13} className="text-ink-400 shrink-0" />
                    ) : (
                      <ArrowRight size={13} className="text-ink-300 shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default CommandPalette;
